'use client';

import type { Content, RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { StackedHeaderStatementBoxes as StackedHeaderStatementBoxesShell } from '@/components/redesign/StackedHeaderStatementBoxes';

/**
 * Manually shaped primary until `prismicio-types.d.ts` regenerates and picks
 * up the `arrowStatements` variation added to `model.json`.
 */
type ArrowStatementsPrimary = {
  theme: 'light' | 'dark' | null;
  title: RichTextField;
  subtitle: RichTextField;
  description: RichTextField;
  remove_upper_margin: boolean;
  statements: Array<{ statement: RichTextField }>;
};

export type StackedHeaderStatementBoxesArrowStatementsProps =
  SliceComponentProps<Content.StackedHeaderStatementBoxesSlice>;

const StackedHeaderStatementBoxesArrowStatements = ({
  slice,
}: StackedHeaderStatementBoxesArrowStatementsProps): JSX.Element => {
  const primary = slice.primary as unknown as ArrowStatementsPrimary;
  const {
    theme,
    title,
    subtitle,
    description,
    remove_upper_margin,
    statements,
  } = primary;
  const isDark = theme === 'dark';
  const textColor = isDark ? 'text-white' : 'text-[#041118]';

  const visible = (statements ?? []).filter((item) =>
    isFilled.richText(item.statement),
  );

  return (
    <StackedHeaderStatementBoxesShell
      sliceType={slice.slice_type}
      variation={slice.variation}
      theme={theme}
      title={title}
      subtitle={subtitle}
      description={description}
      removeUpperMargin={remove_upper_margin}
    >
      {visible.length > 0 ? (
        <ul className="flex w-full flex-col">
          {visible.map((item, index) => (
            <li key={index} className="flex items-start gap-3">
              <span
                aria-hidden
                className="shrink-0 text-[18px] font-bold leading-[1.5] text-[#4b0fea]"
              >
                →
              </span>
              <div className={`min-w-0 flex-1 ${textColor}`}>
                <PrismicRichText
                  field={item.statement}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[18px] font-normal leading-[1.5]">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-bold">{children}</strong>
                    ),
                    em: ({ children }) => (
                      <em className="italic">{children}</em>
                    ),
                    hyperlink: ({ children, node }) => (
                      <a
                        href={node.data.url ?? '#'}
                        className="text-[#4b0fea] underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {children}
                      </a>
                    ),
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </StackedHeaderStatementBoxesShell>
  );
};

export default StackedHeaderStatementBoxesArrowStatements;
